// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { create } from 'zustand';
import { persist, type PersistOptions } from 'zustand/middleware';
import type { AlarmContextSeed } from '../types';

/** Carbon theme keys used by the app shell (dark / light). */
export type CarbonTheme = 'g100' | 'g10';

/** Role-persona that drives the shell layout (mock's Operator/Manager/Executive). */
export type Persona = 'operator' | 'manager' | 'executive';

/**
 * Ask Helios panel width bounds (px). The default matches the Asset Details
 * panel footprint (22rem) so the two right-docked panels read as one family;
 * the user can drag the left edge between the min and max. The max is capped
 * again at the current window width in `setAskHeliosWidth` so the panel can
 * never grow past the viewport on a small screen.
 */
export const ASK_HELIOS_DEFAULT_WIDTH = 352;
export const ASK_HELIOS_MIN_WIDTH = 320;
export const ASK_HELIOS_MAX_WIDTH = 720;

/**
 * The same, for the Asset Details panel. Its default is the 22rem footprint the
 * stylesheet used before it became resizable, and its min is narrower than Ask
 * Helios's: this panel is a list of readings rather than a chat, so it stays
 * legible squeezed.
 */
export const ASSET_PANEL_DEFAULT_WIDTH = 352;
export const ASSET_PANEL_MIN_WIDTH = 280;
export const ASSET_PANEL_MAX_WIDTH = 880;

/**
 * Clamp a dragged or restored panel width to its bounds, capping the max at the
 * current window width too — a wide saved preference must not leave the panel
 * wider than a smaller screen. `window` is guarded for non-DOM environments
 * (SSR / some test setups).
 */
const clampPanelWidth = (px: number, min: number, max: number): number => {
  const viewportMax =
    typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : max;
  return Math.max(min, Math.min(Math.min(max, viewportMax), Math.round(px)));
};

/**
 * The one time zone policy: every clock and timestamp reads on the active
 * site's zone ('site', default) or on the viewer's own ('browser').
 */
export type TimeZonePolicy = 'site' | 'browser';

export interface A11yPrefs {
  highContrast: boolean;
  reduceMotion: boolean;
  largeText: boolean;
  soundAlerts: boolean;
}

/**
 * A Live chart's y-axis preference for one site. `fit` re-enables auto-zoom to
 * the data; otherwise the axis is fixed to `min`..`max`. Absent = the default
 * fixed range derived from the plan.
 */
export interface YAxisPref {
  fit: boolean;
  min?: number;
  max?: number;
}

/** Key of a chart's y-axis preference: one entry per site and card. */
export const yAxisKey = (siteId: string, card: string): string => `${siteId}:${card}`;

export interface ShellState {
  viewMode: 'chat' | 'dashboard';
  selectedSiteId: string | null;
  selectedSiteName: string | null;
  /** True when a request couldn't reach the backend at all (e.g. connection refused). */
  backendUnreachable: boolean;

  // New Carbon shell state
  theme: CarbonTheme;
  a11y: A11yPrefs;
  /** Which zone every time renders on; read by `SiteTimeContext`. */
  timeZonePolicy: TimeZonePolicy;
  /** Dev/manual persona override; when null, persona derives from the auth role. */
  personaOverride: Persona | null;
  subnavOpen: boolean;
  askHeliosOpen: boolean;
  /**
   * On-screen alarm the Ask Helios panel is scoped to. Threaded
   * into the first ask-helios request as ``context_seed`` so the orchestrator
   * resolves the bound SOP directly. Null when opened without an alarm.
   */
  askHeliosSeed: AlarmContextSeed | null;
  /**
   * Docked width of the Ask Helios panel in px. Persisted (unlike the open
   * flag) so the user's drag-to-resize preference survives reloads, and read by
   * `AssetDetailPanelHost` to offset the asset panel when both are open.
   */
  askHeliosWidth: number;
  /** Docked width of the Asset Details panel in px. Persisted, like the above. */
  assetPanelWidth: number;
  /** Per-site, per-card y-axis preferences for the Live charts. Persisted. */
  yAxisPrefs: Record<string, YAxisPref>;
  selectedAssetId: string | null;
  selectedAssetSiteId: string | null;
  pageAlarmContext: AlarmContextSeed | null;

  setViewMode: (_mode: 'chat' | 'dashboard') => void;
  toggleViewMode: () => void;
  setSelectedSite: (_id: string | null, _name?: string | null) => void;
  setBackendUnreachable: (_unreachable: boolean) => void;

  setTheme: (_theme: CarbonTheme) => void;
  toggleTheme: () => void;
  setA11y: (_patch: Partial<A11yPrefs>) => void;
  setTimeZonePolicy: (_policy: TimeZonePolicy) => void;
  setPersonaOverride: (_persona: Persona | null) => void;
  setSubnavOpen: (_open: boolean) => void;
  toggleSubnav: () => void;
  openAskHelios: (_seed?: AlarmContextSeed | null) => void;
  closeAskHelios: () => void;
  toggleAskHelios: () => void;
  clearAskHeliosSeed: () => void;
  setAskHeliosWidth: (_px: number) => void;
  setAssetPanelWidth: (_px: number) => void;
  setYAxisPref: (_key: string, _pref: YAxisPref) => void;
  selectAsset: (_id: string, _siteId: string) => void;
  clearSelectedAsset: () => void;
  setPageAlarmContext: (_seed: AlarmContextSeed | null) => void;
}

type ShellPersist = Pick<
  ShellState,
  | 'viewMode'
  | 'selectedSiteId'
  | 'selectedSiteName'
  | 'theme'
  | 'a11y'
  | 'timeZonePolicy'
  | 'personaOverride'
  | 'askHeliosWidth'
  | 'assetPanelWidth'
  | 'yAxisPrefs'
>;

const persistOptions: PersistOptions<ShellState, ShellPersist> = {
  name: 'helios_shell_state',
  partialize: state => ({
    viewMode: state.viewMode,
    selectedSiteId: state.selectedSiteId,
    selectedSiteName: state.selectedSiteName,
    theme: state.theme,
    a11y: state.a11y,
    timeZonePolicy: state.timeZonePolicy,
    personaOverride: state.personaOverride,
    askHeliosWidth: state.askHeliosWidth,
    assetPanelWidth: state.assetPanelWidth,
    yAxisPrefs: state.yAxisPrefs,
  }),
};

const DEFAULT_A11Y: A11yPrefs = {
  highContrast: true,
  reduceMotion: false,
  largeText: false,
  soundAlerts: false,
};

export const useShellStore = create<ShellState>()(
  persist(
    set => ({
      viewMode: 'chat',
      selectedSiteId: null,
      selectedSiteName: 'Fleet View',
      backendUnreachable: false,

      theme: 'g100',
      a11y: DEFAULT_A11Y,
      timeZonePolicy: 'site',
      personaOverride: null,
      subnavOpen: true,
      askHeliosOpen: false,
      askHeliosSeed: null,
      askHeliosWidth: ASK_HELIOS_DEFAULT_WIDTH,
      assetPanelWidth: ASSET_PANEL_DEFAULT_WIDTH,
      yAxisPrefs: {},
      selectedAssetId: null,
      selectedAssetSiteId: null,
      pageAlarmContext: null,

      setViewMode: mode => set({ viewMode: mode }),
      toggleViewMode: () =>
        set(state => ({
          viewMode: state.viewMode === 'chat' ? 'dashboard' : 'chat',
        })),
      setSelectedSite: (id, name) => set({ selectedSiteId: id, selectedSiteName: name ?? null }),
      setBackendUnreachable: unreachable => set({ backendUnreachable: unreachable }),

      setTheme: theme => set({ theme }),
      toggleTheme: () => set(state => ({ theme: state.theme === 'g100' ? 'g10' : 'g100' })),
      setA11y: patch => set(state => ({ a11y: { ...state.a11y, ...patch } })),
      setTimeZonePolicy: timeZonePolicy => set({ timeZonePolicy }),
      setPersonaOverride: persona => set({ personaOverride: persona }),
      setSubnavOpen: open => set({ subnavOpen: open }),
      toggleSubnav: () => set(state => ({ subnavOpen: !state.subnavOpen })),
      openAskHelios: seed => set({ askHeliosOpen: true, askHeliosSeed: seed ?? null }),
      closeAskHelios: () => set({ askHeliosOpen: false, askHeliosSeed: null }),
      toggleAskHelios: () => set(state => ({ askHeliosOpen: !state.askHeliosOpen })),
      clearAskHeliosSeed: () => set({ askHeliosSeed: null }),
      setAskHeliosWidth: px =>
        set({
          askHeliosWidth: clampPanelWidth(px, ASK_HELIOS_MIN_WIDTH, ASK_HELIOS_MAX_WIDTH),
        }),
      setAssetPanelWidth: px =>
        set({
          assetPanelWidth: clampPanelWidth(px, ASSET_PANEL_MIN_WIDTH, ASSET_PANEL_MAX_WIDTH),
        }),

      setYAxisPref: (key, pref) =>
        set(state => ({ yAxisPrefs: { ...state.yAxisPrefs, [key]: pref } })),

      selectAsset: (id, siteId) =>
        set(
          id && siteId
            ? { selectedAssetId: id, selectedAssetSiteId: siteId }
            : { selectedAssetId: null, selectedAssetSiteId: null }
        ),
      clearSelectedAsset: () => set({ selectedAssetId: null, selectedAssetSiteId: null }),
      setPageAlarmContext: seed => set({ pageAlarmContext: seed }),
    }),
    persistOptions
  )
);
